import { useState } from 'react';

export default function FinalCTA() {
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [showBriefForm, setShowBriefForm] = useState(false);
  const [consultationFormData, setConsultationFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [briefFormData, setBriefFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: ''
  });
  const [consultationSubmitStatus, setConsultationSubmitStatus] = useState('');
  const [briefSubmitStatus, setBriefSubmitStatus] = useState('');

  const handleConsultationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!consultationFormData.name || !consultationFormData.email || !consultationFormData.company) {
      setConsultationSubmitStatus('Please fill in all required fields');
      return;
    }

    try {
      const formBody = new URLSearchParams();
      Object.entries(consultationFormData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6ajjhdbd17lru29lsd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setConsultationSubmitStatus('Thank you! We\'ll be in touch shortly.');
        setConsultationFormData({ name: '', email: '', company: '', phone: '', message: '' });
        setTimeout(() => {
          setShowConsultationForm(false);
          setConsultationSubmitStatus('');
        }, 2000);
      } else {
        setConsultationSubmitStatus('Something went wrong. Please try again.');
      }
    } catch (error) {
      setConsultationSubmitStatus('Error submitting form. Please try again.');
    }
  };

  const handleBriefSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!briefFormData.name || !briefFormData.email || !briefFormData.company) {
      setBriefSubmitStatus('Please fill in all required fields');
      return;
    }

    try {
      const formBody = new URLSearchParams();
      Object.entries(briefFormData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6ajjhdbd17lru29lsdg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setBriefSubmitStatus('Success! Check your email for the executive brief.');
        setBriefFormData({ name: '', email: '', company: '', role: '' });
        setTimeout(() => {
          setShowBriefForm(false);
          setBriefSubmitStatus('');
        }, 2000);
      } else {
        setBriefSubmitStatus('Something went wrong. Please try again.');
      }
    } catch (error) {
      setBriefSubmitStatus('Error submitting form. Please try again.');
    }
  };

  return (
    <section id="final-cta" className="py-24 bg-blue-50">
      <div className="max-w-[800px] mx-auto px-20 text-center">
        <h2 className="text-5xl font-bold text-slate-900 mb-6">
          Ready to Navigate AI with Confidence?
        </h2>
        <p className="text-xl text-slate-600 mb-12">
          Let's define your path forward.
        </p>

        <div className="flex items-center justify-center gap-5 mb-8">
          <button
            onClick={() => setShowConsultationForm(true)}
            className="px-10 py-5 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-all duration-200 text-lg font-medium cursor-pointer whitespace-nowrap shadow-lg"
          >
            Book a Strategy Consultation
          </button>
          <button
            onClick={() => setShowBriefForm(true)}
            className="px-10 py-5 bg-white text-slate-900 rounded-full hover:bg-slate-100 transition-all duration-200 text-lg font-medium cursor-pointer whitespace-nowrap shadow-md"
          >
            Request an Executive Brief
          </button>
        </div>

        <div className="flex items-center justify-center gap-6 opacity-60">
          <img src="https://public.readdy.ai/ai/img_res/9301fd064b6a4f5cb8f791598bfb7dc9.jpg" alt="Partner" className="h-8 grayscale" />
          <img src="https://readdy.ai/api/search-image?query=minimalist%20grayscale%20company%20logo%20simple%20abstract%20corporate%20brand%20mark&width=120&height=60&seq=logo-2&orientation=landscape" alt="Partner" className="h-8 grayscale" />
          <img src="https://readdy.ai/api/search-image?query=minimalist%20grayscale%20company%20logo%20simple%20abstract%20corporate%20brand%20mark&width=120&height=60&seq=logo-3&orientation=landscape" alt="Partner" className="h-8 grayscale" />
          <img src="https://readdy.ai/api/search-image?query=minimalist%20grayscale%20company%20logo%20simple%20abstract%20corporate%20brand%20mark&width=120&height=60&seq=logo-4&orientation=landscape" alt="Partner" className="h-8 grayscale" />
          <img src="https://readdy.ai/api/search-image?query=minimalist%20grayscale%20company%20logo%20simple%20abstract%20corporate%20brand%20mark&width=120&height=60&seq=logo-5&orientation=landscape" alt="Partner" className="h-8 grayscale" />
        </div>
      </div>

      {showConsultationForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-10 max-w-lg w-full relative">
            <button
              onClick={() => setShowConsultationForm(false)}
              className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-900 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <h3 className="text-3xl font-bold text-slate-900 mb-2">Book a Strategy Consultation</h3>
            <p className="text-slate-600 mb-8">Let's discuss your AI transformation journey.</p>

            <form id="consultation-form" data-readdy-form onSubmit={handleConsultationSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={consultationFormData.name}
                  onChange={(e) => setConsultationFormData({ ...consultationFormData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={consultationFormData.email}
                  onChange={(e) => setConsultationFormData({ ...consultationFormData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Company *</label>
                <input
                  type="text"
                  name="company"
                  value={consultationFormData.company}
                  onChange={(e) => setConsultationFormData({ ...consultationFormData, company: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={consultationFormData.phone}
                  onChange={(e) => setConsultationFormData({ ...consultationFormData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                <textarea
                  name="message"
                  value={consultationFormData.message}
                  onChange={(e) => setConsultationFormData({ ...consultationFormData, message: e.target.value })}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 resize-none"
                ></textarea>
                <p className="text-xs text-slate-500 mt-1">{consultationFormData.message.length}/500 characters</p>
              </div>

              {consultationSubmitStatus && (
                <div className={`p-4 rounded-lg ${consultationSubmitStatus.includes('Thank you') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {consultationSubmitStatus}
                </div>
              )}

              <button
                type="submit"
                className="w-full px-8 py-4 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-all duration-200 font-medium cursor-pointer whitespace-nowrap"
              >
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}

      {showBriefForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-10 max-w-lg w-full relative">
            <button
              onClick={() => setShowBriefForm(false)}
              className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-900 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <h3 className="text-3xl font-bold text-slate-900 mb-2">Request Executive Brief</h3>
            <p className="text-slate-600 mb-8">Get our comprehensive guide to AI adoption.</p>

            <form id="brief-form" data-readdy-form onSubmit={handleBriefSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={briefFormData.name}
                  onChange={(e) => setBriefFormData({ ...briefFormData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={briefFormData.email}
                  onChange={(e) => setBriefFormData({ ...briefFormData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Company *</label>
                <input
                  type="text"
                  name="company"
                  value={briefFormData.company}
                  onChange={(e) => setBriefFormData({ ...briefFormData, company: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Role</label>
                <input
                  type="text"
                  name="role"
                  value={briefFormData.role}
                  onChange={(e) => setBriefFormData({ ...briefFormData, role: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              {briefSubmitStatus && (
                <div className={`p-4 rounded-lg ${briefSubmitStatus.includes('Success') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {briefSubmitStatus}
                </div>
              )}

              <button
                type="submit"
                className="w-full px-8 py-4 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-all duration-200 font-medium cursor-pointer whitespace-nowrap"
              >
                Get Executive Brief
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
